import { LogIn } from 'lucide-react';
import { useState } from 'react';
import { useAdminUserLoginAttempts } from '../hooks/useAdminUserLoginAttempts';
import type { AdminLoginAttemptDto } from '../types/admin-user.types';
import type { DataTableColumn } from '@/shared/components/ui/data-table';
import { DataTable } from '@/shared/components/ui/data-table';
import { StatusBadge } from '@/shared/components/ui/status-badge';
import { formatAdminDateTime } from '@/shared/utils/format-date.utils';

const PAGE_SIZE = 10;

const columns: DataTableColumn<AdminLoginAttemptDto>[] = [
  {
    key: 'success',
    header: 'Resultado',
    render: (row) => (
      <StatusBadge
        status={row.success ? 'active' : 'error'}
        label={row.success ? 'Exitoso' : 'Fallido'}
      />
    ),
    className: 'max-w-[8rem]',
  },
  {
    key: 'attemptedAt',
    header: 'Fecha y hora',
    render: (row) => <span>{formatAdminDateTime(row.attemptedAt)}</span>,
  },
];

interface AdminUserLoginAttemptsTabProps {
  adminUserId: string;
}

export function AdminUserLoginAttemptsTab({ adminUserId }: AdminUserLoginAttemptsTabProps) {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError } = useAdminUserLoginAttempts(adminUserId, page, PAGE_SIZE);

  if (isLoading) {
    return <div className="py-8" data-testid="admin-users-login-attempts-loading" aria-hidden />;
  }

  if (isError) {
    return (
      <p className="text-sm text-negative py-4" data-testid="admin-users-login-attempts-error">
        Error al cargar el historial de inicios de sesión.
      </p>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <p
        className="py-8 text-center text-sm text-primary"
        data-testid="admin-users-login-attempts-empty"
      >
        No hay inicios de sesión registrados.
      </p>
    );
  }

  return (
    <div data-testid="admin-users-login-attempts-table">
      <DataTable
        title="Inicios de sesión"
        titleIcon={<LogIn className="w-4 h-4 text-primary" />}
        subtitle={`${data.totalCount} registros`}
        rows={data.items}
        columns={columns}
        getRowKey={(row) => row.id}
        testIdPrefix="admin-users-login-attempts"
        pagination={{
          page: data.page,
          pageSize: data.pageSize,
          totalCount: data.totalCount,
          totalPages: data.totalPages,
          hasPreviousPage: data.hasPreviousPage,
          hasNextPage: data.hasNextPage,
          firstItemOnPage: data.firstItemOnPage,
          lastItemOnPage: data.lastItemOnPage,
        }}
        onPageChange={setPage}
      />
    </div>
  );
}
